import React from "react";
import { Play, Clock, GraduationCap } from "lucide-react";
import { SEO } from "../components/SEO";

const courses = [
  {
    title: "The Craft of the Political Biography",
    instructor: "Kunal Prakash Jha & Sanjeev Chaudhary",
    duration: "8 weeks · 24 lessons",
    price: "₹6,499",
    level: "Intermediate",
    palette: "#721817",
    desc: "How to reconstruct a life through primary sources, interviews, and constituency-level data.",
  },
  {
    title: "Writing India: Non-Fiction from the Field",
    instructor: "CBH Editorial Board",
    duration: "6 weeks · 18 lessons",
    price: "₹4,999",
    level: "Beginner",
    palette: "#0F2A5E",
    desc: "Longform non-fiction for journalists, researchers, and civil servants entering book-length work.",
  },
  {
    title: "The Ayurvedic Editor's Workshop",
    instructor: "Bharat Wisdom Faculty",
    duration: "4 weeks · 12 lessons",
    price: "₹3,999",
    level: "All Levels",
    palette: "#8B1D1B",
    desc: "How to translate ancient Indian wisdom traditions for contemporary global readerships.",
  },
  {
    title: "Election Analysis: A Data-First Approach",
    instructor: "Kunal Prakash Jha",
    duration: "5 weeks · 15 lessons",
    price: "₹5,499",
    level: "Advanced",
    palette: "#E76F1A",
    desc: "Constituency-level modelling, booth-analysis, and how to write election books that hold up.",
  },
  {
    title: "The Diaspora Storyteller",
    instructor: "Kunal Prakash Jha",
    duration: "6 weeks · 18 lessons",
    price: "₹4,999",
    level: "Intermediate",
    palette: "#1F6478",
    desc: "Enterprise stories, migration histories, and how to hold cultural authenticity across borders.",
  },
  {
    title: "Cultural Governance for Public Servants",
    instructor: "Dr. Shravan Kumar (IRS)",
    duration: "10 weeks · 30 lessons",
    price: "₹8,999",
    level: "Advanced",
    palette: "#1F5A3A",
    desc: "A rare course by a serving officer on culture, cinema, and the mechanics of nation-building.",
  },
];

export default function Studio() {
  return (
    <div data-testid="studio-page">
      <SEO
        title="Conlang Studio — Cohort Courses"
        path="/studio"
        description="Learn from the house. Cohort-based writing, editing, and publishing courses taught by CBH's authors and editors."
        keywords="writing courses India, publishing masterclass, biography writing course, cohort courses"
      />
      {/* Hero */}
      <section className="pt-16 lg:pt-24 pb-16 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Conlang Studio</p>
            <h1 className="font-editorial text-6xl md:text-8xl leading-[0.9] tracking-tighter text-ink text-balance">
              Learn from the <em className="text-crimson not-italic">house.</em>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="text-lg leading-relaxed text-ink-muted">
              CBH&rsquo;s authors and editors, in a classroom. Cohort-based courses for writers,
              researchers, and public servants.
            </p>
          </div>
        </div>
      </section>

      {/* Featured video card */}
      <section className="py-16" data-testid="studio-featured-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 md:col-span-7">
              <div className="aspect-video bg-ink relative group overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1437751068958-82e6fccc9360?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600"
                  alt="Featured lecture"
                  className="w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity duration-500"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="w-20 h-20 rounded-full bg-ivory/95 flex items-center justify-center hover:scale-110 transition-transform duration-500" data-testid="studio-play-featured" aria-label="Play featured video">
                    <Play strokeWidth={1.25} fill="#721817" className="w-8 h-8 text-crimson ml-1" />
                  </button>
                </div>
              </div>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="text-[11px] uppercase tracking-[0.32em] text-gold-dark mb-4">Featured Lecture · Free</p>
              <h2 className="font-editorial text-4xl md:text-5xl text-ink leading-tight mb-4">
                What makes a life <em className="text-crimson not-italic">worth writing?</em>
              </h2>
              <p className="text-ink-muted leading-relaxed">
                A 32-minute masterclass from CBH&rsquo;s biography desk — free, in full, and
                foundational to every long-form course in the Studio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Course grid */}
      <section className="py-24 bg-ivory-soft grain-overlay" data-testid="studio-courses-section">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">§ Cohort Courses</p>
              <h2 className="font-editorial text-5xl text-ink leading-tight">The full syllabus.</h2>
            </div>
            <p className="text-sm uppercase tracking-[0.24em] text-ink-muted">6 active courses</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((c, i) => (
              <article
                key={i}
                data-testid={`course-card-${i}`}
                className="bg-ivory border border-crimson/15 p-8 group hover:border-crimson transition-colors duration-500"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-6" style={{ backgroundColor: c.palette }}>
                  <GraduationCap strokeWidth={1.25} className="w-7 h-7 text-ivory" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-gold-dark mb-3">{c.level}</p>
                <h3 className="font-editorial text-2xl text-ink leading-tight group-hover:text-crimson transition-colors duration-500">{c.title}</h3>
                <p className="editorial-italic text-sm text-ink-muted mt-2">with {c.instructor}</p>
                <p className="mt-4 text-ink-soft text-sm leading-relaxed">{c.desc}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-ink-muted">
                  <span className="inline-flex items-center gap-1"><Clock strokeWidth={1.25} className="w-3.5 h-3.5" /> {c.duration}</span>
                </div>
                <div className="mt-8 flex items-center justify-between border-t border-crimson/15 pt-6">
                  <span className="font-editorial text-2xl text-crimson">{c.price}</span>
                  <button className="text-xs uppercase tracking-[0.24em] text-ink hover:text-crimson link-underline">Enroll →</button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
